import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type PointerEvent,
} from 'react';
import { ArrowRight, Check, ChevronRight, FileText, X } from 'lucide-react';

import type { PageContent } from '@/content/pages';
import { cn } from '@/lib/utils';
import { BookDemoButton } from '@/components/ui/book-demo-dialog';
import { Dialog } from '@/components/ui/dialog';
import './treasury-workflow.css';

// Steps default to four two-second scenes, with longer durations and final
// scene holds available when more reading time is needed. One clock drives
// the mockup and progress bar so pausing cannot leave them out of sync.
const DEFAULT_STEP_TIMING = { durationMs: 8_000, sceneCount: 4 };
const PLAYBACK_TICK_MS = 100;
const MIN_VISIBLE_FRACTION = 0.2;
// Match the breakpoint at which the shared layout places steps beside the preview.
const INLINE_POLICY_QUERY = '(min-width: 1024px)';

type Workflow = NonNullable<PageContent['workflow']>;
type WorkflowScene = { step: number; phase: number };
type WorkflowStepTiming = {
  durationMs: number;
  sceneCount: number;
  /** Extra milliseconds on the final scene before advancing to the next step. */
  finalSceneHoldMs?: number;
};
type HoverArea = 'preview' | 'policy';

function getStepDurationMs(timing: WorkflowStepTiming) {
  return timing.durationMs + (timing.finalSceneHoldMs ?? 0);
}

type PlaybackOptions = {
  stepCount: number;
  policyDialogOpen: boolean;
  pauseOnKeyboardFocus: boolean;
  stepTimings: readonly WorkflowStepTiming[] | undefined;
};

/**
 * Advance only while on screen in a visible tab and motion is allowed.
 * Hover pauses until the pointer leaves; keyboard focus can also hold playback
 * when configured. Choosing a step starts it from the beginning. An open mobile
 * policy dialog temporarily holds the same clock; the inline desktop policy
 * does not interrupt playback unless hovered.
 * Each observer, listener, and timer is owned by its effect and cleaned up
 * when its playback condition changes.
 */
function useWorkflowPlayback({
  stepCount,
  policyDialogOpen,
  pauseOnKeyboardFocus,
  stepTimings,
}: PlaybackOptions) {
  const ref = useRef<HTMLDivElement>(null);
  const [playback, setPlayback] = useState({ step: 0, elapsedMs: 0 });
  const [hoveredArea, setHoveredArea] = useState<HoverArea | null>(null);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [inView, setInView] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReducedMotion(query.matches);
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion();
    updateVisibility();
    query.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    return () => {
      query.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(
          !!entry?.isIntersecting &&
            entry.intersectionRatio >= MIN_VISIBLE_FRACTION
        );
      },
      { threshold: [0, MIN_VISIBLE_FRACTION] }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const running =
    !policyDialogOpen &&
    !hoveredArea &&
    (!pauseOnKeyboardFocus || !keyboardFocused) &&
    !reducedMotion &&
    inView &&
    pageVisible;
  useEffect(() => {
    if (!running || stepCount < 2) return;
    const timer = window.setInterval(() => {
      // Functional updates keep manual navigation and timer ticks on the same
      // state, even when a click lands immediately before a tick.
      setPlayback((current) => {
        const elapsedMs = current.elapsedMs + PLAYBACK_TICK_MS;
        const timing = stepTimings?.[current.step] ?? DEFAULT_STEP_TIMING;
        return elapsedMs >= getStepDurationMs(timing)
          ? { step: (current.step + 1) % stepCount, elapsedMs: 0 }
          : { ...current, elapsedMs };
      });
    }, PLAYBACK_TICK_MS);
    return () => window.clearInterval(timer);
  }, [running, stepCount, stepTimings]);

  const stepTiming = stepTimings?.[playback.step] ?? DEFAULT_STEP_TIMING;
  const leaveHover = useCallback((area: HoverArea) => {
    // A hidden policy must release only its own hold, including when no
    // pointer-leave event arrives before its contents disappear.
    setHoveredArea((current) => (current === area ? null : current));
  }, []);

  return {
    ref,
    step: playback.step,
    phase: reducedMotion
      ? stepTiming.sceneCount - 1
      : Math.min(
          stepTiming.sceneCount - 1,
          Math.floor(
            playback.elapsedMs / (stepTiming.durationMs / stepTiming.sceneCount)
          )
        ),
    progress: (playback.elapsedMs / getStepDurationMs(stepTiming)) * 100,
    enterHover: (area: HoverArea) => setHoveredArea(area),
    leaveHover,
    setKeyboardFocused,
    running,
    reducedMotion,
    selectStep: (step: number) => setPlayback({ step, elapsedMs: 0 }),
  };
}

type ProductWorkflowProps = {
  workflow: Workflow;
  label: string;
  shortLabels: readonly string[];
  className: string;
  Mockup: ComponentType<WorkflowScene>;
  /** Whether keyboard navigation also pauses playback; defaults to true. */
  pauseOnKeyboardFocus?: boolean;
  policy?: { title: string; Content: ComponentType<WorkflowScene> };
  /** Scene timing and optional final hold by step; omitted entries use the default. */
  stepTimings?: readonly WorkflowStepTiming[];
};

/**
 * Shared walkthrough navigation and playback keep product pages consistent.
 * Mockups own their scenes; this shell owns visibility, timing, focus, and pause.
 */
export function ProductWorkflow({
  workflow,
  label,
  shortLabels,
  className,
  Mockup,
  pauseOnKeyboardFocus = true,
  policy,
  stepTimings,
}: ProductWorkflowProps) {
  const [policyOpen, setPolicyOpen] = useState(false);
  const [inlinePolicy, setInlinePolicy] = useState(false);
  const policyTrigger = useRef<HTMLButtonElement>(null);
  const hasPolicy = !!policy;
  const playback = useWorkflowPlayback({
    stepCount: workflow.steps.length,
    policyDialogOpen: hasPolicy && policyOpen && !inlinePolicy,
    pauseOnKeyboardFocus,
    stepTimings,
  });
  const panelId = useId();
  const policyPanelId = useId();
  const policyTitleId = useId();
  const leaveHover = playback.leaveHover;
  const closePolicy = useCallback(() => {
    setPolicyOpen(false);
    leaveHover('policy');
    // The modal owns its focus restoration. The inline panel needs its own
    // return path because closing it removes the focused close button.
    if (inlinePolicy) policyTrigger.current?.focus({ preventScroll: true });
  }, [inlinePolicy, leaveHover]);
  const handlePointerEnter = (
    area: HoverArea,
    event: PointerEvent<HTMLElement>
  ) => {
    // Touch taps must not leave a sticky hover pause after dismissing a dialog.
    if (event.pointerType === 'mouse') playback.enterHover(area);
  };

  useEffect(() => {
    if (!hasPolicy) return;
    const query = window.matchMedia(INLINE_POLICY_QUERY);
    const updateLayout = () => {
      setInlinePolicy(query.matches);
      if (!query.matches) leaveHover('policy');
    };
    updateLayout();
    query.addEventListener('change', updateLayout);
    // Moving between layouts transfers the same open policy to the dialog or
    // inline panel. Dialog cleanup releases its focus trap and body scroll lock.
    return () => query.removeEventListener('change', updateLayout);
  }, [hasPolicy, leaveHover]);

  const activeStep = workflow.steps[playback.step];
  if (!activeStep) return null;
  // The policy reads the same clock as the mockup, including its paused frame.
  const policyContent = policy ? (
    <policy.Content step={playback.step} phase={playback.phase} />
  ) : null;

  return (
    <div
      ref={playback.ref}
      className={cn(
        'product-workflow mt-10 sm:mt-12',
        className,
        hasPolicy && 'has-policy',
        hasPolicy && policyOpen && inlinePolicy && 'is-policy-open'
      )}
      data-running={playback.running}
      data-reduced-motion={playback.reducedMotion}
      role="group"
      aria-label={`${label} product walkthrough`}
      onPointerDownCapture={() => {
        if (pauseOnKeyboardFocus) playback.setKeyboardFocused(false);
      }}
      onFocusCapture={(event) => {
        if (pauseOnKeyboardFocus) {
          playback.setKeyboardFocused(event.target.matches(':focus-visible'));
        }
      }}
      onKeyDownCapture={(event) => {
        // The mobile dialog handles Escape itself; portal events also bubble
        // through this shell and must not dismiss it a second time.
        if (event.key === 'Escape' && policyOpen && inlinePolicy) {
          event.preventDefault();
          closePolicy();
        } else if (pauseOnKeyboardFocus) {
          playback.setKeyboardFocused(true);
        }
      }}
      onBlurCapture={(event) => {
        // Moving between controls keeps the keyboard pause until focus leaves.
        if (
          pauseOnKeyboardFocus &&
          !event.currentTarget.contains(event.relatedTarget)
        ) {
          playback.setKeyboardFocused(false);
        }
      }}
      style={
        { '--workflow-progress': `${playback.progress}%` } as CSSProperties
      }
    >
      <div className="twf-layout">
        <div className="twf-navigation">
          <ol className="twf-steps" aria-label={`${label} workflow steps`}>
            {workflow.steps.map((step, index) => (
              <li key={step.title}>
                <button
                  type="button"
                  className={cn(
                    'twf-step',
                    index === playback.step && 'is-active'
                  )}
                  aria-current={index === playback.step ? 'step' : undefined}
                  aria-controls={panelId}
                  aria-label={`Step ${index + 1}: ${step.title}`}
                  onClick={() => playback.selectStep(index)}
                >
                  <span className="twf-step-number" aria-hidden="true">
                    {index < playback.step ? <Check /> : index + 1}
                  </span>
                  <span className="twf-step-content">
                    <span className="twf-step-title">{step.title}</span>
                    <span className="twf-step-short" aria-hidden="true">
                      {shortLabels[index]}
                    </span>
                  </span>
                  <span className="twf-step-progress" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
          {policy && (
            <button
              ref={policyTrigger}
              type="button"
              className="workflow-policy-trigger"
              aria-expanded={policyOpen}
              aria-controls={
                policyOpen || inlinePolicy ? policyPanelId : undefined
              }
              aria-haspopup={inlinePolicy ? undefined : 'dialog'}
              onClick={policyOpen ? closePolicy : () => setPolicyOpen(true)}
            >
              <FileText aria-hidden="true" />
              {policyOpen ? 'Hide policy' : 'View policy'}
              <ChevronRight
                className="workflow-policy-chevron"
                aria-hidden="true"
              />
            </button>
          )}
        </div>

        {policy && inlinePolicy && (
          <aside
            id={policyPanelId}
            className="workflow-policy-panel"
            aria-labelledby={policyTitleId}
            aria-hidden={!policyOpen}
            inert={!policyOpen}
            onPointerEnter={(event) => handlePointerEnter('policy', event)}
            onPointerLeave={() => leaveHover('policy')}
          >
            {policyOpen && (
              <div className="workflow-policy-inner">
                <div className="workflow-policy-heading">
                  <div>
                    <p>Policy in this walkthrough</p>
                    <h3 id={policyTitleId}>{policy.title}</h3>
                  </div>
                  <button
                    type="button"
                    onClick={closePolicy}
                    aria-label="Close policy"
                  >
                    <X aria-hidden="true" />
                  </button>
                </div>
                {policyContent}
              </div>
            )}
          </aside>
        )}

        <div
          id={panelId}
          className="twf-preview"
          role="region"
          aria-label="Current workflow step"
          onPointerEnter={(event) => handlePointerEnter('preview', event)}
          onPointerLeave={() => leaveHover('preview')}
        >
          <div className="twf-mobile-heading">
            {workflow.steps.map((step, index) => (
              <div
                key={step.title}
                className={index === playback.step ? 'is-active' : undefined}
                aria-hidden={index !== playback.step}
              >
                <h3>{step.title}</h3>
              </div>
            ))}
          </div>
          <Mockup step={playback.step} phase={playback.phase} />
        </div>

        <div className="twf-cta">
          <BookDemoButton variant="primary" analyticsLocation="page_workflow">
            Book a demo
            <ArrowRight className="size-4" aria-hidden="true" />
          </BookDemoButton>
        </div>
      </div>
      {policy && !inlinePolicy && (
        <Dialog
          open={policyOpen}
          onClose={closePolicy}
          returnFocusRef={policyTrigger}
          labelledById={policyTitleId}
          className="workflow-policy-dialog"
        >
          <div id={policyPanelId}>
            <div className="workflow-policy-heading">
              <div>
                <p>Policy in this walkthrough</p>
                <h3 id={policyTitleId}>{policy.title}</h3>
              </div>
            </div>
            {policyContent}
          </div>
        </Dialog>
      )}
    </div>
  );
}
